import nordUk from "../config/nordUk.js";
import { Button } from "../components/UI/button.js";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import callAPI from "../api/index.js";
import ReactGA from 'react-ga4';

const translations = {
    en: {
        invalidAccess: "We didn't receive the necessary information to process your request.",
        needsRetry: {
            line1: "It looks like your account setup was not completed.",
            line2: "Please retry the process to complete your registration",
            line3: "and start using NordVPN."
        },
        hasError: {
            line1: "It looks like your account setup was not completed.",
            line2: "Please log in to your account to finish the registration process",
            line3: "and start using NordVPN."
        },
        success: {
            line1: "Thank you for registering your",
            line2: "NordVPN account",
            line3: "Please log in into your account"
        },
        buttons: {
            retry: "Retry Registration",
            tryAgain: "Try Again",
            accessProfile: "Access my profile"
        },
        unsubscribe: "Unsubscribe?"
    },
    fr: {
        invalidAccess: "Nous n'avons pas reçu les informations nécessaires pour traiter votre demande.",
        needsRetry: {
            line1: "Il semble que la configuration de votre compte n'ait pas été terminée.",
            line2: "Veuillez réessayer le processus pour terminer votre inscription",
            line3: "et commencer à utiliser NordVPN."
        },
        hasError: {
            line1: "Il semble que la configuration de votre compte n'ait pas été terminée.",
            line2: "Veuillez vous connecter à votre compte pour terminer le processus d'inscription",
            line3: "et commencer à utiliser NordVPN."
        },
        success: {
            line1: "Merci d'avoir enregistré votre",
            line2: "compte NordVPN",
            line3: "Veuillez vous connecter à votre compte"
        },
        buttons: {
            retry: "Réessayer l'inscription",
            tryAgain: "Réessayer",
            accessProfile: "Accéder à mon profil"
        },
        unsubscribe: "Se désabonner ?"
    },
    bg: {
        invalidAccess: "Не получихме необходимата информация за обработка на вашата заявка.",
        needsRetry: {
            line1: "Изглежда, че настройката на вашия акаунт не е завършена.",
            line2: "Моля, повторете процеса, за да завършите регистрацията си",
            line3: "и да започнете да използвате NordVPN."
        },
        hasError: {
            line1: "Изглежда, че настройката на вашия акаунт не е завършена.",
            line2: "Моля, влезте в акаунта си, за да завършите процеса на регистрация",
            line3: "и да започнете да използвате NordVPN."
        },
        success: {
            line1: "Благодарим ви, че регистрирахте вашия",
            line2: "NordVPN акаунт",
            line3: "Моля, влезте в акаунта си"
        },
        buttons: {
            retry: "Повторете регистрацията",
            tryAgain: "Опитайте отново",
            accessProfile: "Достъп до моя профил"
        },
        unsubscribe: "Отписване?"
    },
    sv: {
        invalidAccess: "Vi fick inte den nödvändiga informationen för att behandla din förfrågan.",
        needsRetry: {
            line1: "Det verkar som att din kontokonfiguration inte slutfördes.",
            line2: "Vänligen försök igen för att slutföra din registrering",
            line3: "och börja använda NordVPN."
        },
        hasError: {
            line1: "Det verkar som att din kontokonfiguration inte slutfördes.",
            line2: "Vänligen logga in på ditt konto för att slutföra registreringsprocessen",
            line3: "och börja använda NordVPN."
        },
        success: {
            line1: "Tack för att du registrerade ditt",
            line2: "NordVPN-konto",
            line3: "Vänligen logga in på ditt konto"
        },
        buttons: {
            retry: "Försök registrera igen",
            tryAgain: "Försök igen",
            accessProfile: "Öppna min profil"
        },
        unsubscribe: "Avprenumerera?"
    }
};

const languageNames = {
    en: "EN",
    fr: "FR",
    bg: "BG",
    sv: "SV"
};

export default function Success(_) {
    const config = nordUk;
    const location = useLocation();

    const [hasError, setHasError] = useState(false);
    const [registrationUrl, setRegistrationUrl] = useState(null);
    const [needsRetry, setNeedsRetry] = useState(false);
    const [invalidAccess, setInvalidAccess] = useState(false);
    const [language, setLanguage] = useState(location.state?.language || 'en');
    const [showLanguageMenu, setShowLanguageMenu] = useState(false);

    const t = translations[language];

    useEffect(() => {
        const handleError = async () => {
            const urlParams = new URLSearchParams(window.location.search)
            const registration_id = urlParams.get('registration_uuid');
            const subscription_id = urlParams.get('subscription_uuid');
            const user_id = urlParams.get('user_uuid');
            const error = urlParams.get('error');
            const isRegistered = urlParams.get('registered');
            console.log(registration_id, subscription_id, user_id)

            ReactGA.send({ hitType: "pageview", page: window.location.pathname });

            if (registration_id && subscription_id && user_id) {
                const res = await callAPI({
                    registration_id: registration_id,
                    user_id: user_id,
                    subscription_id: subscription_id,
                    error: error,
                }, 'confirmRegistration');
                if (res?.status === "FAIL") {
                    setNeedsRetry(true);
                    setRegistrationUrl("https://dcb-checkout.com")
                }
            }
            else if (error) {
                setHasError(true);

                const res = await callAPI({
                    registration_id: registration_id
                }, 'getRegistrationUrl');
                if (res?.payload?.registration_url) {
                    setRegistrationUrl(res.payload.registration_url)
                }
            
            } else if (!isRegistered) {
                setInvalidAccess(true);
            }
        };
        handleError();
    }, [])

    const handleClick = () => {
        if (hasError || needsRetry) {
            if (registrationUrl) {
                window.open(registrationUrl, "_self");
            } else {
                console.error("No registration URL available");
            }
        }
        else {
            window.open("https://my.nordaccount.com/", "_self");
        }
    };

    return (
        <>
            <div
                className="nord min-h-screen flex sm:flex-row overflow-hidden"
                style={{
                    backgroundColor: config.background_color,
                    color: config.text_color,
                    fontFamily: config.font_family,
                }}
            >
                <div className="md:w-1/2 bg-blue-500  justify-center items-center relative hidden sm:flex w-1/4">
                    <img
                        src={config.featured_image}
                        className="max-w-full h-auto w-[80%]"
                        alt="Featured"
                    />
                </div>
                <div className="w-full sm:w-3/4 md:w-1/2 flex flex-col justify-between items-start bg-white max-h-screen overflow-y-auto">
                    <div className="w-full flex justify-center items-center">
                        <div className="flex items-center justify-between w-full">
                            <div className="w-1/3 flex justify-center items-center"></div>
                            <div className="w-1/3 flex justify-center items-center">
                                {config.logo_url && !config.logo_under_button && (
                                    <img
                                        src={config.logo_url}
                                        alt={"Logo"}
                                        id="Logo"
                                        className={`${config.logo_size === "Small"
                                            ? "w-[300px] lg:w-[200px] md:w-[200px] max-w-none"
                                            : ""
                                        }`}
                                    />
                                )}
                            </div>
                            <div className="w-1/3 flex justify-end items-center mr-1 relative">
                                <button
                                    onClick={() => setShowLanguageMenu(!showLanguageMenu)}
                                    className="flex items-center gap-1 md:gap-2 px-2 md:px-3 py-1.5 md:py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-sm md:text-base"
                                >
                                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
                                    </svg>
                                    <span className="font-semibold">{languageNames[language]}</span>
                                    <svg className="w-3 h-3 md:w-4 md:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </button>

                                {showLanguageMenu && (
                                    <div className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 min-w-[100px]">
                                        {Object.entries(languageNames).map(([code, name]) => (
                                            <button
                                                key={code}
                                                onClick={() => {
                                                    setLanguage(code);
                                                    setShowLanguageMenu(false);
                                                }}
                                                className={`w-full text-center px-4 py-2 hover:bg-gray-100 transition-colors font-semibold ${
                                                    language === code ? 'bg-blue-50 text-blue-600' : ''
                                                }`}
                                            >
                                                {name}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="w-full flex-col justify-center items-center px-4">
                        <div className="w-full flex justify-center items-center">
                            <h2
                                className="text-center font-bold lg:text-3xl md:text-2xl text-xl leading-snug"
                                style={{
                                    color: config.titles_color || "#000",
                                }}
                            >
                                {invalidAccess
                                    ? t.invalidAccess
                                    : needsRetry
                                        ? <>
                                            {t.needsRetry.line1}
                                            <br />{t.needsRetry.line2}
                                            <br />{t.needsRetry.line3}
                                        </>
                                        : hasError
                                            ? <>
                                                {t.hasError.line1}
                                                <br />{t.hasError.line2}
                                                <br />{t.hasError.line3}
                                            </>
                                            : <>
                                                {t.success.line1} <br /> {t.success.line2}
                                                <br />
                                                <br /> {t.success.line3}
                                            </>
                                }
                            </h2>
                        </div>

                        {!invalidAccess && (
                            <div className="w-full flex flex-col items-center justify-center mt-10">
                                <div className="w-full flex md:flex-row lg:flex-row md:justify-center items-center gap-8 mx-auto max-w-md">
                                    <Button
                                        className={`flex flex-row gap-2 lg:h-[98px]  h-[70px] px-5 py-4 w-full  font-semibold text-lg rounded-full tracking-wider
                    md:!text-xl lg:!text-2xl`}
                                        style={{
                                            backgroundColor: config.button_primary_color || "#e02f1fe3",
                                            color: config.button_text_color || "#000",
                                            fontSize: config.button_font_size,
                                        }}
                                        onClick={handleClick}
                                    >
                                        {needsRetry ? t.buttons.retry : hasError ? t.buttons.tryAgain : t.buttons.accessProfile}
                                    </Button>
                                </div>
                            </div>
                        )}


                    </div>

                    <div className="w-full flex justify-center items-center relative sm:hidden hidden">
                        <img
                            src={config.featured_image}
                            className="max-w-full h-auto w-[50%]"
                            alt="Featured"
                        />
                    </div>

                    <div className="w-full flex justify-center items-center text-center text-sm md:text-base" style={{color: '#000', fontSize: '12px'}}>
                        <u><Link to={'/nord-vpn/unsub'} state={{ language }} style={{color: '#000', fontSize: '12px'}}>{t.unsubscribe}</Link></u>
                    </div>

                    <div
                        className="text-center w-full flex-col items-center justify-center  bg-black
           p-3"
                    >

                        <div className="flex flex-col items-center ">
                            {config.show_footer && (
                                <>
                                    <div
                                        className="flex gap-2 items-center "
                                        dangerouslySetInnerHTML={{
                                            __html: config.footer_content || "",
                                        }}
                                    ></div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div >
        </>
    );
}